/**
 * Yönetici işlevler: Londra Kulesi ve kurallı labirent.
 *
 * İkisinde de ölçülen şey planlama: hamleyi yapmadan önce sonucunu
 * görmek. Farkları yükün nerede olduğu — kulede ileriye doğru zihinsel
 * simülasyon, kurallı labirentte bir kuralı yolun tamamı boyunca akılda
 * tutmak (dürtü kontrolü).
 *
 * ÜRETİMİN İŞİ YİNE AYNI: sayfanın gerçekten çözülebilir olduğunu ve
 * kuralın görevi gerçekten değiştirdiğini garanti etmek. Kurallı
 * labirentte bu ikincisi bedava değil: kural, en kısa yolu geçersiz
 * kılmıyorsa çocuk kuralı hiç okumadan doğru cevabı veriyor ve biz
 * "dürtü kontrolünü çalıştırdık" sanıyoruz.
 */

import { Rng } from '../../prng'
import { SHAPES_SIMPLE, carveMaze, solveMaze, yolGecerliMi } from '../helpers'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { CellNode, ExerciseTemplate, GradeResult, RenderNode } from '../types'
import { SEKIL_ADI } from '../../render/shapes'
import type { ShapeName } from '../../render/shapes'

/* ================================================================== *
 * 30. Londra Kulesi
 *
 * Üç çubuk, birkaç top; bir başlangıç ve bir hedef durum. Kural: tek
 * seferde yalnızca en üstteki top taşınır.
 *
 * TOPLAR RENKLE DEĞİL ŞEKİLLE ayrışıyor (§0.1). Klasik testte renkli
 * toplar var ama kâğıt kanalımız siyah-beyaz çıkıyor: renk ayrımı
 * fotokopide kayboluyor ve görev "hangi top neredeydi" sorusuna bile
 * cevap veremez hale geliyor.
 *
 * HEDEF, BAŞLANGIÇTAN TAM `hamle` UZAKTA. Rastgele bir hedef seçip
 * "kaç hamle" diye sormak, düzeyin anlamını tohuma bırakıyordu: aynı
 * düzeyde bir sayfa iki hamlede, öteki yedi hamlede bitiyordu. Hedef
 * genişlik öncelikli aramayla, başlangıçtan tam istenen uzaklıkta
 * seçiliyor — yani "en az kaç hamle" sorusunun cevabı düzeyin kendisi.
 * ================================================================== */
export const towerOfLondon: ExerciseTemplate = {
  code: 'tower_of_london',
  title: 'Londra Kulesi',
  domains: ['working_memory_visual', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Top sayısı */
      top: l >= 7 ? 4 : 3,
      /** Çözümün hamle sayısı */
      hamle: scale(l, 2, 7),
      /** Çubuk sayısı sabit: üçten fazlası planlama değil, dağınıklık */
      cubuk: 3,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const topSayisi = p['top'] as number
    const istenenHamle = p['hamle'] as number
    const cubuk = p['cubuk'] as number

    const toplar = rng.shuffle(SHAPES_SIMPLE).slice(0, topSayisi)

    // Başlangıç: toplar rastgele dağıtılmış
    const bas: ShapeName[][] = Array.from({ length: cubuk }, () => [])
    for (const t of rng.shuffle(toplar)) bas[rng.int(0, cubuk - 1)]!.push(t)

    const { uzaklik, geri } = aramaAgaci(bas, cubuk, topSayisi)
    // Tam istenen uzaklıktaki durumlar; yoksa en uzağa düş
    const adaylar = [...uzaklik.entries()].filter(([, d]) => d === istenenHamle)
    const enUzak = Math.max(...uzaklik.values())
    const havuz =
      adaylar.length > 0
        ? adaylar
        : [...uzaklik.entries()].filter(([, d]) => d === enUzak)
    const [hedefAnahtar, gercekHamle] = rng.pick(havuz)

    const hedef = anahtardanDurum(hedefAnahtar)
    const cozum = yolCikar(hedefAnahtar, geri)

    const yukseklik = topSayisi
    const izgara = (durum: ShapeName[][]): CellNode[] => {
      const cells: CellNode[] = []
      for (let r = 0; r < yukseklik; r++) {
        for (let c = 0; c < cubuk; c++) {
          // Satır 0 en üst: yığın aşağıdan dolduğu için tersten okuyoruz
          const yigin = durum[c]!
          const derinlik = yukseklik - 1 - r
          const top = yigin[derinlik]
          cells.push(top ? { shape: top } : { blank: true })
        }
      }
      return cells
    }

    return {
      code: 'tower_of_london',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: { ...p, hamle: gercekHamle },
      domains: this.domains,
      /**
       * Hamle yazımı yönergede AÇIKÇA tarif ediliyor.
       *
       * İlk provada sayfada "1. hamle ____" satırları vardı ama ne
       * yazılacağı hiçbir yerde yazmıyordu; çubukların numarası da yok.
       * Çocuk doğru planı kurup yanlış biçimde yazabilirdi ve biz onu
       * planlama hatası diye okurduk.
       */
      prompt:
        `Üstteki düzeni alttaki düzene çevir. Her hamlede yalnızca bir çubuğun EN ÜSTTEKİ ` +
        `topunu alıp başka bir çubuğa koyabilirsin. Hamleleri "2 → 3" gibi yaz: önce topu ` +
        `aldığın çubuk, sonra koyduğun çubuk. En az ${gercekHamle} hamlede yapılabilir.`,
      spokenPrompt:
        'Topları taşıyarak alttaki düzeni kuracaksın. Her seferinde yalnızca en üstteki topu alabilirsin.',
      render: [
        { t: 'text', value: 'Başlangıç', size: 'sm', bold: true },
        { t: 'grid', cols: cubuk, rows: yukseklik, cells: izgara(bas), border: true },
        {
          // Çubuk numaraları ızgaranın altında: bir top yuvasıyla
          // karışmasın diye ayrı bir satır değil, ayrı bir düğüm
          t: 'text',
          value: Array.from({ length: cubuk }, (_, i) => `${i + 1}. çubuk`).join('     '),
          size: 'sm',
        },
        { t: 'text', value: 'Hedef', size: 'sm', bold: true },
        { t: 'grid', cols: cubuk, rows: yukseklik, cells: izgara(hedef), border: true },
        {
          /**
           * Kâğıtta hamleler satır satır yazılıyor: "1. çubuktan 3.
           * çubuğa". Ekranda bu satırlar kullanılmıyor, çocuk tahtada
           * taşıyor — bir çocuğa hamle dizisi yazdırmak, ölçtüğümüz
           * şeye yazı yazma yükü eklemek olurdu.
           */
          t: 'rows',
          items: Array.from({ length: gercekHamle }, (_, i) => ({
            label: `${i + 1}. hamle`,
            blank: true,
            id: 'h' + i,
          })),
          columns: 2,
        },
      ],
      solution: { bas, hedef, hamleler: cozum, enAz: gercekHamle },
      grading: 'auto',
      estimatedSeconds: 40 + gercekHamle * 20,
      itemCount: 2,
    }
  },
  /**
   * İki şey ayrı ayrı puanlanıyor: hedefe ulaştı mı, en az hamleyle mi.
   *
   * Tek bir doğru/yanlış, "hedefe ulaştı ama dolambaçlı gitti" ile
   * "hiç ulaşamadı"yı aynı gösteriyordu; ikisi iki ayrı bulgu ve
   * planlama için asıl ilginç olan birincisi.
   */
  grade(instance, answer): GradeResult {
    const sol = instance.solution as {
      bas: ShapeName[][]
      hedef: ShapeName[][]
      enAz: number
    }
    const hamleler = (Array.isArray(answer) ? answer : []) as unknown[]
    const durum = sol.bas.map((y) => [...y])
    let gecerli = true
    let sayi = 0
    for (const h of hamleler) {
      const ikili = Array.isArray(h) ? h.map(Number) : null
      if (!ikili || ikili.length !== 2) {
        gecerli = false
        break
      }
      const [a, b] = ikili as [number, number]
      if (a < 0 || b < 0 || a >= durum.length || b >= durum.length || a === b) {
        gecerli = false
        break
      }
      const top = durum[a]!.pop()
      if (!top) {
        gecerli = false
        break
      }
      durum[b]!.push(top)
      sayi++
    }
    const ulasti = gecerli && durumAnahtari(durum) === durumAnahtari(sol.hedef)
    const enAz = ulasti && sayi === sol.enAz
    return {
      correct: (ulasti ? 1 : 0) + (enAz ? 1 : 0),
      total: 2,
      accuracy: ((ulasti ? 1 : 0) + (enAz ? 1 : 0)) / 2,
      perItem: [ulasti, enAz],
      /**
       * Hiç hamle yapılmadıysa `verilen` BOŞ.
       *
       * "Denedi, ulaşamadı" ile "hiç denemedi" iki ayrı bulgu ve
       * uygulayıcının ayırt edebilmesi gerekiyor (bkz. `ItemResult`).
       * İlk hali boş cevaba da "ulaşamadı" yazıyordu.
       */
      items: [
        {
          id: 'hedef',
          beklenen: 'hedef düzene ulaşmak',
          verilen:
            hamleler.length === 0 ? null : gecerli ? (ulasti ? 'ulaştı' : 'ulaşamadı') : 'kuralsız hamle',
          dogru: ulasti,
        },
        {
          id: 'enaz',
          beklenen: `${sol.enAz} hamle`,
          verilen: hamleler.length === 0 || !gecerli ? null : `${sayi} hamle`,
          dogru: enAz,
        },
      ],
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { hamleler: [number, number][]; enAz: number }
    const satir = sol.hamleler.map(([a, b], i) => `${i + 1}. ${a + 1}→${b + 1}`).join('  ')
    return `En az ${sol.enAz} hamle. Bir çözüm: ${satir} (çubuk numaraları soldan sağa)`
  },
}

/* ---------- kule: durum arama ---------- */

function durumAnahtari(durum: ShapeName[][]): string {
  return durum.map((y) => y.join(',')).join('|')
}

function anahtardanDurum(a: string): ShapeName[][] {
  return a.split('|').map((y) => (y ? (y.split(',') as ShapeName[]) : []))
}

/**
 * Başlangıçtan ulaşılabilen bütün durumlar ve uzaklıkları.
 *
 * Durum sayısı küçük (dört top, üç çubuk için birkaç bin), o yüzden
 * tamamı taranıyor. Hedefi rastgele seçip "kaç hamle" diye sormak
 * yerine tam istenen uzaklıktan seçmenin yolu bu.
 */
function aramaAgaci(
  bas: ShapeName[][],
  cubuk: number,
  _top: number,
): { uzaklik: Map<string, number>; geri: Map<string, [string, [number, number]]> } {
  const basAnahtar = durumAnahtari(bas)
  const uzaklik = new Map<string, number>([[basAnahtar, 0]])
  const geri = new Map<string, [string, [number, number]]>()
  const kuyruk = [basAnahtar]
  while (kuyruk.length) {
    const simdi = kuyruk.shift()!
    const durum = anahtardanDurum(simdi)
    for (let a = 0; a < cubuk; a++) {
      if (durum[a]!.length === 0) continue
      for (let b = 0; b < cubuk; b++) {
        if (a === b) continue
        const yeni = durum.map((y) => [...y])
        yeni[b]!.push(yeni[a]!.pop()!)
        const anahtar = durumAnahtari(yeni)
        if (uzaklik.has(anahtar)) continue
        uzaklik.set(anahtar, uzaklik.get(simdi)! + 1)
        geri.set(anahtar, [simdi, [a, b]])
        kuyruk.push(anahtar)
      }
    }
  }
  return { uzaklik, geri }
}

function yolCikar(
  hedef: string,
  geri: Map<string, [string, [number, number]]>,
): [number, number][] {
  const out: [number, number][] = []
  let simdi = hedef
  while (geri.has(simdi)) {
    const [onceki, hamle] = geri.get(simdi)!
    out.unshift(hamle)
    simdi = onceki
  }
  return out
}

/* ================================================================== *
 * 31. Kurallı labirent
 *
 * Kutularda sayılar var; çıkışa ulaşırken toplamları verilen sınırı
 * geçmeyecek. Yani en kısa yol doğru cevap DEĞİL.
 *
 * KURAL GERÇEKTEN BAĞLAYICI OLMAK ZORUNDA ve bu şablonun asıl işi o.
 * Sınır gelişigüzel seçilseydi en kısa yol çoğu tohumda zaten sınırın
 * altında kalırdı; çocuk kuralı hiç okumadan doğru cevabı verir, biz de
 * dürtü kontrolünü çalıştırdığımızı sanırdık. Üretim iki şeyi birden
 * garanti ediyor: (1) sınırı sağlayan bir yol VAR, (2) en kısa yol
 * sınırı AŞIYOR.
 *
 * Sayılar da renk değil (§0.1): rakam fotokopide de duruyor.
 * ================================================================== */
export const ruleMaze: ExerciseTemplate = {
  code: 'rule_maze',
  title: 'Sayılı labirent',
  domains: ['inhibition', 'working_memory_visual'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      cols: scale(l, 4, 8),
      rows: scale(l, 4, 8),
      /** Ek açıklık: birden fazla yol olmadan kural anlamsız */
      extraOpenings: scale(l, 4, 12),
      /** Kutulardaki en büyük sayı */
      enbuyuk: scale(l, 3, 6),
      /**
       * Doğru yol, en kısa yoldan en fazla bu kadar uzun olabilir.
       *
       * Sınırsız bırakıldığında en ucuz yol 7×7'lik bir labirentte 31
       * kutu dolanıyordu: Dijkstra toplamı küçültüyor, uzunluğu
       * umursamıyor ve küçük sayıların peşinde sayfayı turluyor.
       * Çocuğa otuz toplama yaptırmak dürtü kontrolü değil aritmetik.
       */
      sapma: scale(l, 3, 6),
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const cols = p['cols'] as number
    const rows = p['rows'] as number
    const enbuyuk = p['enbuyuk'] as number

    let kurgu: {
      m: ReturnType<typeof carveMaze>
      sayilar: number[]
      ucuz: number[]
      sinir: number
    } | null = null

    for (let deneme = 0; deneme < 120 && !kurgu; deneme++) {
      const rng = new Rng(Number(seed) + deneme * 7919)
      const m = carveMaze(rng, cols, rows, p['extraOpenings'] as number)
      const kisa = solveMaze(m)
      if (!kisa) continue

      /**
       * Sayılar rastgele DEĞİL, kurularak dağıtılıyor.
       *
       * İlk hali her kutuya rastgele bir sayı koyup "en kısa yol
       * sınırı aşıyor mu" diye bakıyor, aşmıyorsa tohumu atıyordu —
       * ve küçük ızgaralarda çoğu tohumda aşmıyordu, çünkü en kısa yol
       * aynı zamanda en ucuzu oluyordu. Üretim yüz yirmi denemede
       * kurgu bulamıyordu. Aritmetik ağda aynı hatayı bir kez yaptık:
       * çözüm kümesinin boş olabildiği yerde reddet-ve-tekrar-dene
       * kullanılmaz.
       *
       * Kurulum basit: en kısa yolun kutuları BÜYÜK sayı alıyor,
       * ötekiler küçük. Böylece kural yapı gereği bağlayıcı oluyor ve
       * çocuğun göreceği şey de doğru: hızlı giden yol pahalı.
       */
      const buyukTaban = Math.max(2, Math.ceil(enbuyuk * 0.6))
      const kucukTavan = Math.max(1, Math.floor(enbuyuk * 0.5))
      const kisaKume = new Set(kisa)
      const sayilar = Array.from({ length: cols * rows }, (_, i) =>
        kisaKume.has(i) ? rng.int(buyukTaban, enbuyuk) : rng.int(1, kucukTavan),
      )
      // Giriş ve çıkış bedava: çocuk zaten orada başlıyor ve bitiriyor
      sayilar[m.start] = 0
      sayilar[m.end] = 0

      const ucuz = enUcuzYol(m, sayilar, kisa.length + (p['sapma'] as number))
      if (!ucuz) continue
      const ucuzToplam = ucuz.reduce((a, i) => a + sayilar[i]!, 0)
      const kisaToplam = kisa.reduce((a, i) => a + sayilar[i]!, 0)

      /**
       * Sınır, iki toplamın ARASINDA.
       *
       * Ucuz yolun toplamına eşit ya da biraz üstü: ucuz yol geçerli
       * kalıyor. En kısa yolun toplamının ALTINDA: kuralı okumayan
       * çocuk sınırı aşıyor. İkisi arasında yer yoksa bu tohum atılıyor
       * — zorlamak yerine başka bir labirent denemek daha dürüst.
       */
      if (kisaToplam <= ucuzToplam + 1) continue
      const sinir = ucuzToplam + Math.min(2, kisaToplam - ucuzToplam - 1)
      if (sinir >= kisaToplam) continue
      // Sınır iki basamağı geçmesin: üç basamaklı bir bütçe, yolda
      // tutulacak sayıyı çocuğun işleyen belleğinden taşırıyor ve
      // görev planlama olmaktan çıkıp toplama alıştırmasına dönüyor
      if (sinir > 99) continue
      kurgu = { m, sayilar, ucuz, sinir }
    }
    if (!kurgu) throw new Error('rule_maze: kurgu bulunamadi')

    const { m, sayilar, ucuz, sinir } = kurgu
    const cells: CellNode[] = m.walls.map((w, i) => ({
      walls: w,
      id: 'r' + i,
      marked: i === m.start || i === m.end,
      glyph: i === m.start ? 'A' : i === m.end ? 'B' : String(sayilar[i]),
    }))

    return {
      code: 'rule_maze',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: { ...p, sinir },
      domains: this.domains,
      prompt:
        `A'dan B'ye giden yolu çiz. Geçtiğin kutulardaki sayıların toplamı ${sinir}'i ` +
        `GEÇMESİN. En kısa yol bu kuralı sağlamıyor.`,
      spokenPrompt:
        'Yolu çizerken kutulardaki sayıları topla. Toplam verilen sayıyı geçmeyecek, o yüzden en kısa yol işe yaramıyor.',
      render: [{ t: 'grid', cols, rows, cells, border: true }],
      solution: { yol: ucuz, sinir, sayilar, start: m.start, end: m.end },
      grading: 'auto',
      estimatedSeconds: Math.round(ucuz.length * 4),
      itemCount: 2,
    }
  },
  /**
   * İki şey ayrı ayrı: çıkışa ulaştı mı, kurala uydu mu.
   *
   * Tek bir doğru/yanlış, "yolu buldu ama toplamı aştı" ile "yolu hiç
   * bulamadı"yı aynı gösteriyordu. Birincisi tam olarak ölçmek
   * istediğimiz şey: dürtü kontrolü.
   */
  grade(instance, answer): GradeResult {
    const sol = instance.solution as {
      sinir: number
      sayilar: number[]
      start: number
      end: number
    }
    const yol = (Array.isArray(answer) ? answer : []).map(Number).filter((x) => !Number.isNaN(x))
    const izgara = (instance.render as RenderNode[]).find((n) => n.t === 'grid')
    const gecerli =
      izgara !== undefined &&
      izgara.t === 'grid' &&
      yol[0] === sol.start &&
      yol[yol.length - 1] === sol.end &&
      yolGecerliMi(izgara.cells, izgara.cols, yol)
    const toplam = yol.reduce((a, i) => a + (sol.sayilar[i] ?? 0), 0)
    const kurala = gecerli && toplam <= sol.sinir
    return {
      correct: (gecerli ? 1 : 0) + (kurala ? 1 : 0),
      total: 2,
      accuracy: ((gecerli ? 1 : 0) + (kurala ? 1 : 0)) / 2,
      perItem: [gecerli, kurala],
      items: [
        {
          id: 'yol',
          beklenen: 'A’dan B’ye geçerli bir yol',
          verilen: yol.length ? `${yol.length} kutu` : null,
          dogru: gecerli,
        },
        {
          id: 'kural',
          beklenen: `toplam ${sol.sinir} veya altı`,
          verilen: gecerli ? String(toplam) : null,
          dogru: kurala,
        },
      ],
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { yol: number[]; sinir: number; sayilar: number[] }
    const cols = instance.params['cols'] as number
    const toplam = sol.yol.reduce((a, i) => a + (sol.sayilar[i] ?? 0), 0)
    const yaz = (i: number) => `${Math.floor(i / cols) + 1}.${(i % cols) + 1}`
    return (
      `Sınır ${sol.sinir}. Bir çözüm (satır.sütun, toplam ${toplam}): ` +
      sol.yol.map(yaz).join(' → ')
    )
  },
}

/**
 * Uzunluğu sınırlı, en düşük toplamlı yol.
 *
 * En KISA yol değil: kuralın bağlayıcı olup olmadığı tam olarak bu
 * ikisinin farkında. Ama sınırsız bir "en ucuz" da olmaz — düz
 * Dijkstra toplamı küçültmek için sayfayı turluyordu ve cevap otuz
 * kutuluk bir yol çıkıyordu. Burada adım sayısı da bir boyut:
 * `maliyet[adim][kutu]`, en fazla `enFazlaAdim` adımda.
 */
function enUcuzYol(
  m: ReturnType<typeof carveMaze>,
  sayilar: number[],
  enFazlaAdim: number,
): number[] | null {
  const n = m.cols * m.rows
  const SONSUZ = Number.POSITIVE_INFINITY
  const maliyet: number[][] = Array.from({ length: enFazlaAdim + 1 }, () =>
    new Array<number>(n).fill(SONSUZ),
  )
  const onceki: number[][] = Array.from({ length: enFazlaAdim + 1 }, () =>
    new Array<number>(n).fill(-1),
  )
  maliyet[0]![m.start] = sayilar[m.start]!

  const komsular = (i: number): number[] => {
    const [ust, sag, alt, sol] = m.walls[i]!
    const out: number[] = []
    if (!ust) out.push(i - m.cols)
    if (!sag) out.push(i + 1)
    if (!alt) out.push(i + m.cols)
    if (!sol) out.push(i - 1)
    return out.filter((k) => k >= 0 && k < n)
  }

  for (let adim = 0; adim < enFazlaAdim; adim++) {
    for (let i = 0; i < n; i++) {
      if (maliyet[adim]![i] === SONSUZ) continue
      for (const k of komsular(i)) {
        const yeni = maliyet[adim]![i]! + sayilar[k]!
        if (yeni < maliyet[adim + 1]![k]!) {
          maliyet[adim + 1]![k] = yeni
          onceki[adim + 1]![k] = i
        }
      }
    }
  }

  let enIyi = SONSUZ
  let enIyiAdim = -1
  for (let adim = 1; adim <= enFazlaAdim; adim++) {
    if (maliyet[adim]![m.end]! < enIyi) {
      enIyi = maliyet[adim]![m.end]!
      enIyiAdim = adim
    }
  }
  if (enIyiAdim < 0) return null

  const yol: number[] = []
  let i = m.end
  let adim = enIyiAdim
  while (adim >= 0 && i !== -1) {
    yol.unshift(i)
    if (adim === 0) break
    i = onceki[adim]![i]!
    adim--
  }
  // Aynı kutudan iki kez geçen yol, puanlama tarafında geçersiz
  return yol[0] === m.start && new Set(yol).size === yol.length ? yol : null
}

/** Cevap anahtarında şekil adları okunur dursun diye. */
export function kuleTopAdi(s: ShapeName): string {
  return SEKIL_ADI[s]
}
